/**
 * clientExport.ts — client-side PDF (print) and XLSX (Excel HTML) generators.
 * No external dependencies: PDF uses browser print-to-PDF, XLSX uses Excel XML.
 */

const BRAND = 'Quantum Kairoz';

function stamp() {
  return `Exported ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`;
}

/* ─── Shared HTML shell ──────────────────────────────────────────────────── */
function printShell(title: string, subtitle: string, body: string): string {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>
<style>
  body{font-family:'Segoe UI',Arial,sans-serif;padding:32px;color:#1a1a2e;background:#fff}
  .brand{font-size:11px;color:#6899A0;margin-bottom:16px;letter-spacing:0.5px}
  h1{font-size:20px;font-weight:700;margin:0 0 4px}
  .sub{font-size:12px;color:#64748b;margin:0 0 20px}
  .stamp{font-size:11px;color:#94a3b8;margin-bottom:24px}
  table{border-collapse:collapse;width:100%}
  th{background:#f8fafc;border:1px solid #e2e8f0;padding:8px 12px;font-size:11px;text-align:left;text-transform:uppercase;letter-spacing:0.4px;color:#64748b}
  td{border:1px solid #e2e8f0;padding:7px 12px;font-size:12px;vertical-align:top}
  tr:nth-child(even) td{background:#fafafa}
  .kv td:first-child{font-weight:600;color:#475569;width:35%;background:#f8fafc}
  @media print{body{padding:0}button{display:none}}
</style></head>
<body>
<div class="brand">${BRAND}</div>
<h1>${title}</h1>
${subtitle ? `<p class="sub">${subtitle}</p>` : ''}
<p class="stamp">${stamp()}</p>
${body}
<script>window.onload=()=>{window.print();}</` + `script>
</body></html>`;
}

/* ─── CSV helpers ────────────────────────────────────────────────────────── */
function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

function buildCsv(headers: string[], rows: string[][]): string {
  return [headers.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))].join('\n');
}

function download(content: string, mime: string, filename: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

function openPrint(html: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (win) win.onafterprint = () => URL.revokeObjectURL(url);
}

/* ─── Public API ─────────────────────────────────────────────────────────── */

/** Export a key-value record (e.g. single audit / batch / SOP) as print-PDF */
export function exportRecordPDF(
  title: string,
  subtitle: string,
  fields: [string, string | number][],
) {
  const rows = fields
    .map(([k, v]) => `<tr><td>${k}</td><td>${v ?? '—'}</td></tr>`)
    .join('');
  const body = `<table class="kv"><tbody>${rows}</tbody></table>`;
  openPrint(printShell(title, subtitle, body));
}

/** Export a key-value record as CSV (opens natively in Excel) */
export function exportRecordXLSX(filename: string, fields: [string, string | number][]) {
  const csv = buildCsv(['Field', 'Value'], fields.map(([k, v]) => [k, v ?? ''] as [string, string]));
  download(csv, 'text/csv', `${filename}-${new Date().toISOString().slice(0, 10)}.csv`);
}

/** Export a table (columns + rows) as print-PDF */
export function exportTablePDF(
  title: string,
  subtitle: string,
  columns: { key: string; label: string }[],
  data: Record<string, unknown>[],
) {
  const ths = columns.map((c) => `<th>${c.label}</th>`).join('');
  const trs = data
    .map(
      (row) =>
        `<tr>${columns.map((c) => `<td>${row[c.key] ?? '—'}</td>`).join('')}</tr>`,
    )
    .join('');
  const body = `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
  openPrint(printShell(title, subtitle, body));
}

/** Export a table as CSV (opens natively in Excel) */
export function exportTableXLSX(
  filename: string,
  columns: { key: string; label: string }[],
  data: Record<string, unknown>[],
) {
  const headers = columns.map((c) => c.label);
  const rows = data.map((row) => columns.map((c) => row[c.key] ?? '') as (string | number)[]);
  const csv = buildCsv(headers, rows as string[][]);
  download(csv, 'text/csv', `${filename}-${new Date().toISOString().slice(0, 10)}.csv`);
}
